import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ImageUpload";
import CardBuilder, { DEFAULT_LAYOUT } from "@/components/CardBuilder";
import GoogleWalletPreview from "@/components/GoogleWalletPreview";
import { useAuth } from "@/contexts/AuthContext";

import { API } from "@/config";

const PassDesigns = () => {
  const { org } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDesign, setEditingDesign] = useState(null);
  const [formData, setFormData] = useState({
    club_name: "",
    logo_url: "",
    active_color: "#16A34A",
    expired_color: "#71717A",
    text_color: "#FFFFFF",
    banner_image_url: "",
    card_layout: DEFAULT_LAYOUT,
  });
  const [saving, setSaving] = useState(false);
  const [previewExpired, setPreviewExpired] = useState(false);
  const [activeTab, setActiveTab] = useState("design");

  useEffect(() => {
    fetchDesigns();
  }, []);

  const fetchDesigns = async () => {
    try {
      const response = await axios.get(`${API}/pass-designs`);
      setDesigns(response.data);
    } catch (error) {
      console.error("Error fetching designs:", error);
      toast.error("Failed to fetch designs");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (design = null) => {
    if (design) {
      setEditingDesign(design);
      setFormData({
        club_name: design.club_name,
        logo_url: design.logo_url || "",
        active_color: design.active_color || "#16A34A",
        expired_color: design.expired_color || "#71717A",
        text_color: design.text_color || "#FFFFFF",
        banner_image_url: design.banner_image_url || "",
        card_layout: design.card_layout || DEFAULT_LAYOUT,
      });
    } else {
      setEditingDesign(null);
      setFormData({
        club_name: org?.name || "",
        logo_url: "",
        active_color: "#16A34A",
        expired_color: "#71717A",
        text_color: "#FFFFFF",
        banner_image_url: "",
        card_layout: DEFAULT_LAYOUT,
      });
    }
    setPreviewExpired(false);
    setActiveTab("design");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.club_name) {
      toast.error("Club name is required");
      return;
    }
    setSaving(true);
    try {
      const saveData = { ...formData };
      if (!saveData.banner_image_url) delete saveData.banner_image_url;
      if (editingDesign) {
        await axios.put(`${API}/pass-designs/${editingDesign.id}`, saveData);
        toast.success("Design updated!");
      } else {
        await axios.post(`${API}/pass-designs`, saveData);
        toast.success("Design created!");
      }
      setDialogOpen(false);
      fetchDesigns();
    } catch (error) {
      console.error("Error saving design:", error);
      toast.error("Failed to save design");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (designId, designName) => {
    try {
      await axios.delete(`${API}/pass-designs/${designId}`);
      toast.success(`"${designName}" deleted!`);
      fetchDesigns();
    } catch (error) {
      console.error("Error deleting design:", error);
      toast.error("Failed to delete design");
    }
  };

  const tabs = [
    { id: "design", label: "Design" },
    { id: "layout", label: "Card Layout" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-12" data-testid="pass-designs-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900">
            Pass Designs
          </h1>
          <p className="text-base text-zinc-600 mt-2">
            Create and manage designs for different clubs
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
          data-testid="create-design-btn"
        >
          <Plus size={20} weight="bold" className="mr-2" />
          Create Design
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading...</div>
      ) : designs.length === 0 ? (
        <div className="text-center py-12 border border-zinc-200 rounded-md bg-white">
          <p className="text-zinc-500" data-testid="no-designs-message">
            No designs yet. Create your first pass design to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {designs.map((design, index) => (
            <div
              key={design.id}
              className="border border-zinc-200 bg-white rounded-md overflow-hidden hover:shadow-md transition-all"
              data-testid={`design-card-${index}`}
            >
              <div className="p-4 bg-zinc-50">
                <GoogleWalletPreview design={design} compact isExpired={false} />
              </div>
              <div className="p-4">
                <h3 className="text-lg font-medium text-zinc-900 mb-2">
                  {design.club_name}
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-xs text-zinc-500">Active:</div>
                  <div className="w-8 h-8 rounded border border-zinc-200" style={{ backgroundColor: design.active_color }} />
                  <div className="text-xs text-zinc-500">Expired:</div>
                  <div className="w-8 h-8 rounded border border-zinc-200" style={{ backgroundColor: design.expired_color }} />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => handleOpenDialog(design)} size="sm" variant="outline" className="flex-1" data-testid={`edit-design-btn-${index}`}>
                    <Pencil size={16} weight="bold" className="mr-1" /> Edit
                  </Button>
                  <Button onClick={() => handleDelete(design.id, design.club_name)} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-design-btn-${index}`}>
                    <Trash size={16} weight="bold" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto" data-testid="design-dialog">
          <DialogHeader>
            <DialogTitle>{editingDesign ? "Edit Design" : "Create New Design"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-4">
            {/* Left panel: Tabs */}
            <div className="lg:col-span-3 space-y-4">
              <div className="flex gap-1 border-b border-zinc-200" data-testid="builder-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? "border-[var(--brand)] text-[var(--brand)]"
                        : "border-transparent text-zinc-500 hover:text-zinc-700"
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "design" && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="club-name">Club Name *</Label>
                    <Input
                      id="club-name"
                      data-testid="dialog-club-name-input"
                      value={formData.club_name}
                      onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                      className="mt-2"
                      placeholder="Enter club name"
                    />
                  </div>
                  <div>
                    <Label>Logo Image</Label>
                    <div className="mt-2">
                      <ImageUpload
                        value={formData.logo_url}
                        onChange={(url) => setFormData({ ...formData, logo_url: url })}
                        label="Logo"
                        placeholder="Upload or paste logo URL"
                      />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1.5">
                      Recommended: 660×660px PNG, square (1:1). Google Wallet masks this into a circle, so keep
                      your logo within the middle ~70% of the image.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Active Color</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={formData.active_color}
                          onChange={(e) => setFormData({ ...formData, active_color: e.target.value })}
                          className="flex-1 text-xs"
                          data-testid="dialog-active-color-input"
                        />
                        <input
                          type="color"
                          value={formData.active_color}
                          onChange={(e) => setFormData({ ...formData, active_color: e.target.value })}
                          className="w-10 h-10 rounded border border-zinc-200 cursor-pointer"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Expired Color</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={formData.expired_color}
                          onChange={(e) => setFormData({ ...formData, expired_color: e.target.value })}
                          className="flex-1 text-xs"
                          data-testid="dialog-expired-color-input"
                        />
                        <input
                          type="color"
                          value={formData.expired_color}
                          onChange={(e) => setFormData({ ...formData, expired_color: e.target.value })}
                          className="w-10 h-10 rounded border border-zinc-200 cursor-pointer"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Text Color</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={formData.text_color}
                          onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                          className="flex-1 text-xs"
                          data-testid="dialog-text-color-input"
                        />
                        <input
                          type="color"
                          value={formData.text_color}
                          onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                          className="w-10 h-10 rounded border border-zinc-200 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Banner Image (Bottom)</Label>
                    <div className="mt-2">
                      <ImageUpload
                        value={formData.banner_image_url}
                        onChange={(url) => setFormData({ ...formData, banner_image_url: url })}
                        label="Banner"
                        placeholder="Upload or paste banner URL"
                      />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1.5">
                      Recommended: 1032×812px PNG (~5:4). Use a transparent PNG if you want the pass's background
                      colour to show through.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === "layout" && (
                <CardBuilder
                  layout={formData.card_layout}
                  onChange={(layout) => setFormData({ ...formData, card_layout: layout })}
                  design={formData}
                />
              )}
            </div>

            {/* Right panel: Live Preview */}
            <div className="lg:col-span-2">
              <div className="sticky top-0 bg-zinc-50 p-4 rounded-md space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-zinc-900">Live Preview</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewExpired(!previewExpired)}
                    data-testid="toggle-preview-btn"
                  >
                    {previewExpired ? "Show Active" : "Show Expired"}
                  </Button>
                </div>
                <GoogleWalletPreview design={formData} isExpired={previewExpired} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={() => setDialogOpen(false)} variant="outline" data-testid="cancel-design-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
              data-testid="save-design-dialog-btn"
            >
              {saving ? "Saving..." : editingDesign ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PassDesigns;
